import { css, ThemeContext } from '@emotion/react';
import styled from '@emotion/styled';
import { useUID } from 'react-uid';
import { useContext } from 'react';

import { AppTheme } from 'theme';
import colors from 'lib/colors';

import Button from './Button';
import Flex from './Flex';
import Modal from './Modal';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onAfterClose?: () => void;
  title: string;
  body: React.ReactNode;
  buttonLabel?: string;
  onButtonClick?: () => void;
  shouldCloseOnOverlayClick?: boolean;
  shouldCloseOnEsc?: boolean;
}

const AlertDialog = ({
  isOpen,
  onClose,
  onAfterClose,
  title,
  body,
  buttonLabel = 'OK',
  onButtonClick,
  shouldCloseOnOverlayClick,
  shouldCloseOnEsc,
}: Props) => {
  const modalHeadingId = useUID();

  const theme = useContext(ThemeContext) as AppTheme;
  const IS_EDU = theme?.layout?.edu;

  return (
    <Modal
      isOpen={isOpen}
      showClose={
        IS_EDU &&
        (shouldCloseOnEsc !== false || shouldCloseOnOverlayClick !== false)
      }
      onRequestClose={onClose}
      onAfterClose={onAfterClose}
      shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
      shouldCloseOnEsc={shouldCloseOnEsc}
      aria={{
        labelledby: modalHeadingId,
      }}
      css={css`
        .Overlay {
          z-index: 100;
        }
        .Modal {
          background-color: white;
          min-width: 350px;
        }
      `}
    >
      <Flex p={4} flexDirection="column" alignItems="center">
        <Heading id={modalHeadingId}>{title}</Heading>
        <BodyText>{body}</BodyText>
        <Button
          onClick={onButtonClick ?? onClose}
          extraWide
          variant={IS_EDU ? 'primary' : 'warning'}
          label={buttonLabel}
        />
      </Flex>
    </Modal>
  );
};

export default AlertDialog;

const Heading = styled.h1`
  font-weight: bold;
  font-size: 30px;
  color: ${colors.black};
  letter-spacing: 0.46px;
  margin: 0;
`;

const BodyText = styled.p`
  font-size: 1rem;
  color: ${colors.gray};
  text-align: center;
  line-height: 1.35;
  padding: 0;
  margin: 0;
  margin-bottom: 24px;
  margin-top: 24px;
`;
